import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { announcementService } from '../services/announcementService';
import type { BannerFormulario } from '../types/announcement.types';

const CLAVE_ADMIN = ['banners', 'admin'] as const;

/**
 * El archivo completo de banners, para el panel.
 *
 * `staleTime: 0` al revés que la lectura del portal: quien administra acaba de
 * cambiar algo y necesita ver el resultado, no una copia de hace cinco minutos.
 */
export function useBannersAdmin(habilitado: boolean) {
  return useQuery({
    queryKey: CLAVE_ADMIN,
    queryFn: () => announcementService.listarAdmin(),
    enabled: habilitado,
    staleTime: 0,
  });
}

/**
 * Crear, editar, prender/apagar y borrar.
 *
 * Todas invalidan las dos consultas: la del panel y la del portal
 * (`portal-banners`). Sin lo segundo, apagar un banner lo quitaría del listado
 * pero seguiría pintado en el dashboard de la misma pestaña hasta que caduque
 * su caché, que es justo el momento en que uno duda de si el botón funcionó.
 */
export function useBannerMutaciones() {
  const queryClient = useQueryClient();

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: CLAVE_ADMIN });
    queryClient.invalidateQueries({ queryKey: ['portal-banners'] });
  };

  const crear = useMutation({
    mutationFn: (banner: BannerFormulario) => announcementService.crear(banner),
    onSuccess: invalidar,
  });

  const actualizar = useMutation({
    mutationFn: ({ id, cambios }: { id: string; cambios: Partial<BannerFormulario> }) =>
      announcementService.actualizar(id, cambios),
    onSuccess: invalidar,
  });

  const borrar = useMutation({
    mutationFn: (id: string) => announcementService.borrar(id),
    onSuccess: invalidar,
  });

  return { crear, actualizar, borrar };
}

/**
 * Mensaje legible de un fallo de guardado.
 *
 * El backend responde `{ errores: string[] }` en los 400 — son los mensajes de
 * validación, y son lo único que le dice a quien redacta qué corregir. Sin
 * esto, un 400 se vería como "Error" a secas.
 */
export function mensajeDeError(error: unknown): string {
  const data = (error as { response?: { data?: { errores?: string[]; message?: string } } })
    ?.response?.data;
  if (data?.errores?.length) return data.errores.join(' ');
  if (data?.message) return data.message;
  return 'No se pudo guardar. Intenta de nuevo.';
}
